import type {TInstagramContent} from "@/store/types";
import dayjs from "dayjs";
import JSZip from "jszip";
import {useCallback, useMemo} from "react";
import {useAsyncFn} from "react-use";
import useDownloader from "react-use-downloader";

const useContentDownload = (contents: TInstagramContent[], namePrefix: string | null | undefined) => {
  const content = contents[0];
  const isSourceContent = useMemo(() => content?.instagram_media?.media_product_type === "SOURCE", [content]);
  const {download} = useDownloader({
    cache: "no-cache",
  });

  const getFileName = useCallback(() => {
    const sharedFileNameElements =
      (namePrefix?.replaceAll(" ", "_") ?? "content") +
      "_" +
      dayjs(content.instagram_media?.created ?? content.instagram_media?.timestamp).format("DD.MM.YYYY") +
      "_" +
      (content.user?.username ??
        `${content.user?.first_name}${content.user?.last_name ? "_" + content.user.last_name : ""}`);
    if (content.instagram_media?.media_type === "CAROUSEL_ALBUM" || isSourceContent)
      return sharedFileNameElements.replaceAll("/", "_") + ".zip";
    if (content.instagram_media?.media_type === "VIDEO") {
      const videoMedia = content.instagram_media?.media_file!.split("/").pop();
      return sharedFileNameElements + "." + videoMedia?.split(".").at(-1)?.split("?")[0];
    }
    const contentName = content.image.split("/").pop();
    return sharedFileNameElements + "." + contentName?.split(".").at(-1)?.split("?")[0];
  }, [namePrefix, content]);

  const [{loading: isDownloading}, downloadMedia] = useAsyncFn(async () => {
    if (isSourceContent || content.instagram_media?.media_type === "CAROUSEL_ALBUM") {
      const zip = new JSZip();
      const folder = zip.folder(isSourceContent ? "sourceContent" : "carousel");
      const mediaToDownload = isSourceContent
        ? contents.map(c => c.instagram_media)
        : (content.instagram_media?.children ?? []);
      for (let i = 0; i < mediaToDownload!.length; i++) {
        const media = mediaToDownload![i];
        const response = await fetch(media?.media_file!, {cache: "no-cache"});
        const blob = await response.blob();
        let name = media?.media_file?.split("/").pop();
        if (name!.includes("?")) {
          name = name!.split("?")[0];
        }
        name = getFileName().split(".zip")[0] + "_" + (i + 1) + "." + name!.split(".").at(-1);
        folder!.file(name, blob);
      }

      const blobContent = await zip.generateAsync({type: "blob"});
      const blobUrl = URL.createObjectURL(blobContent);
      download(blobUrl, getFileName());
      URL.revokeObjectURL(blobUrl);
      return;
    }

    if (content.instagram_media?.media_type === "IMAGE") {
      //content.image is the same as content.instagram_media.thumbnail_file
      await download(content.image, getFileName());
    }

    if (content.instagram_media?.media_type === "VIDEO") {
      const videoMedia = content.instagram_media?.media_file!;
      await download(videoMedia, getFileName());
    }
  }, [content, download, getFileName]);

  return {downloadMedia, isDownloading};
};

export default useContentDownload;
